// src/pages/organization/members/CreateMember.js

import React, { useState, useEffect } from "react";
import { useMainPageTitle } from "../../../contexts/PageContext";
import { useNavigate, useLocation } from "react-router-dom";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export const CreateMember = () => {
  const { updatePageTitle } = useMainPageTitle();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine base path based on current route
  const basePath = location.pathname.startsWith('/supervisor') ? '/supervisor/members' : '/organization/members';
  
  useEffect(() => {
    updatePageTitle("Create Member", "Add a new member to your organization");
  }, [updatePageTitle]);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState("");
  const [creationType, setCreationType] = useState("justOne");

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [errors, setErrors] = useState({});

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!userName) newErrors.userName = "Username is required";

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Email is not valid";
    }

    if (phone && !/^\d{10,11}$/.test(phone)) {
      newErrors.phone = "Phone should be 10 or 11 digits";
    }

    if (!gender) newErrors.gender = "Gender is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const apiEndpoint = `${API_URL}/supervisor/create-supervisor`;
      console.log("Submitting to:", apiEndpoint);

      const requestData = {
        firstName,
        lastName,
        userName,
        email,
        phone,
        gender,
        creationType,
      };

      console.log("Form data:", requestData);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found. Please log in again.");
      }

      // Add timeout to the fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        const response = await fetch(apiEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestData),
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle response
        if (!response.ok) {
          const data = await response.json();
          console.log("Error response:", data);

          // Handle different error response formats
          if (
            data.errors &&
            Array.isArray(data.errors) &&
            data.errors.length > 0
          ) {
            throw new Error(
              data.errors[0].msg ||
                data.errors[0].message ||
                data.errors[0] ||
                "Validation error"
            );
          } else {
            throw new Error(
              data.error || data.message || "Failed to create member"
            );
          }
        }

        const data = await response.json();
        console.log("Success response:", data);
        if (creationType === "addMulti") {
          // Success
          setMessage({
            text: firstName + " " + lastName + " created successfully!",
            type: "success",
          });
        }
        // Reset form
        setFirstName("");
        setLastName("");
        setUserName("");
        setEmail("");
        setPhone("");
        setGender("");

        if (creationType === "justOne") {
          // Redirect to the new member's page using dynamic path
          navigate(`${basePath}/${data._id}`);
        }
        if (creationType === "addRole") {
          // Redirect to the create role page using dynamic path
          navigate(`${basePath}/create-role/${data._id}`);
        }
      } catch (fetchError) {
        if (fetchError.name === "AbortError") {
          throw new Error(
            "Request timed out. The server took too long to respond."
          );
        }
        throw fetchError;
      }
    } catch (error) {
      console.error("Error details:", error);

      // Provide more user-friendly messages for common errors
      if (error.message.includes("bcrypt is not defined")) {
        setMessage({
          text: "Server configuration error. Please contact the administrator.",
          type: "error",
        });
      } else if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError")
      ) {
        setMessage({
          text: "Network error. Please check your connection and try again.",
          type: "error",
        });
      } else {
        setMessage({
          text: error.message || "An error occurred",
          type: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <button 
          onClick={() => navigate(basePath)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Members
        </button>
      </div>

      {message.text && (
        <div
          className={`p-4 mb-6 rounded-lg shadow-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border-l-4 border-green-500"
              : "bg-red-50 text-red-800 border-l-4 border-red-500"
          }`}
        >
          <div className="flex items-center">
            {message.type === "success" ? (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
              </svg>
            )}
            {message.text}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md overflow-hidden">

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] ${
                  errors.firstName ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
                placeholder="Enter first name"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.firstName}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] ${
                  errors.lastName ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
                placeholder="Enter last name"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.lastName}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="userName"
              className="block text-sm font-medium text-gray-700"
            >
              Username <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                id="userName"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className={`w-full pl-10 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] ${
                  errors.userName ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
                placeholder="Enter username"
              />
            </div>
            {errors.userName && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                </svg>
                {errors.userName}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700"
            >
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full pl-10 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] ${
                  errors.email ? "border-red-500 bg-red-50" : "border-gray-300"
                }`}
                placeholder="Enter email address"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                </svg>
                {errors.email}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={`w-full pl-10 px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] ${
                    errors.phone ? "border-red-500 bg-red-50" : "border-gray-300"
                  }`}
                  placeholder="Enter phone number"
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.phone}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700"
              >
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F15213] ${
                  errors.gender ? "border-red-500 bg-red-50" : "border-gray-300"
                } appearance-none bg-white`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.gender}
                </p>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-8">
            <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-[#F15213]" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path>
              </svg>
              Creation Options
            </h3>

            <div className="space-y-4">
              <label
                htmlFor="justOne"
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                  creationType === "justOne"
                    ? "bg-[#F15213]/10 border-[#F15213]"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="relative flex items-center">
                  <input
                    id="justOne"
                    name="creationType"
                    type="radio"
                    value="justOne"
                    checked={creationType === "justOne"}
                    onChange={(e) => setCreationType(e.target.value)}
                    className="sr-only" // Hide the default radio button
                  />
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                      creationType === "justOne"
                        ? "border-[#F15213]"
                        : "border-gray-400"
                    }`}
                  >
                    {creationType === "justOne" && (
                      <div className="w-3 h-3 rounded-full bg-[#F15213]"></div>
                    )}
                  </div>
                </div>
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-700">
                    Just create one supervisor
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Create a single supervisor without additional configuration
                  </span>
                </div>
              </label>

              <label
                htmlFor="addRole"
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                  creationType === "addRole"
                    ? "bg-[#F15213]/10 border-[#F15213]"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="relative flex items-center">
                  <input
                    id="addRole"
                    name="creationType"
                    type="radio"
                    value="addRole"
                    checked={creationType === "addRole"}
                    onChange={(e) => setCreationType(e.target.value)}
                    className="sr-only" // Hide the default radio button
                  />
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                      creationType === "addRole"
                        ? "border-[#F15213]"
                        : "border-gray-400"
                    }`}
                  >
                    {creationType === "addRole" && (
                      <div className="w-3 h-3 rounded-full bg-[#F15213]"></div>
                    )}
                  </div>
                </div>
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-700">
                    Create supervisor with role assignment
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Create a supervisor and immediately assign roles and permissions
                  </span>
                </div>
              </label>

              <label
                htmlFor="addMulti"
                className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                  creationType === "addMulti"
                    ? "bg-[#F15213]/10 border-[#F15213]"
                    : "bg-white border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="relative flex items-center">
                  <input
                    id="addMulti"
                    name="creationType"
                    type="radio"
                    value="addMulti"
                    checked={creationType === "addMulti"}
                    onChange={(e) => setCreationType(e.target.value)}
                    className="sr-only" // Hide the default radio button
                  />
                  <div
                    className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                      creationType === "addMulti"
                        ? "border-[#F15213]"
                        : "border-gray-400"
                    }`}
                  >
                    {creationType === "addMulti" && (
                      <div className="w-3 h-3 rounded-full bg-[#F15213]"></div>
                    )}
                  </div>
                </div>
                <div className="ml-3">
                  <span className="block text-sm font-medium text-gray-700">
                    Create multiple supervisors
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Create this supervisor and continue adding more members
                  </span>
                </div>
              </label>
            </div>
          </div>

          <div className="pt-6">
            <button
              type="submit"
              className={`w-full py-3 px-4 bg-[#F15213] hover:bg-[#F15213]/90 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#F15213] focus:ring-offset-2 transition-colors ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Member...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd"></path>
                  </svg>
                  Create Member
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMember;
